import React, { useState, useEffect } from 'react';
import { generatePattern, detectPatternConflicts, findControllers, PatternConflict } from '../../services/rosterService';
import { format } from 'date-fns';
import apiClient from '../../services/api';
import { DatePicker } from '../ui/DatePicker';

interface PatternGenerationModalProps {
    onClose: () => void;
    onSuccess: () => void;
    startDate: Date;
    versionId?: string;
}

const PatternGenerationModal: React.FC<PatternGenerationModalProps> = ({
    onClose,
    onSuccess,
    startDate,
    versionId,
}) => {
    const [patternType, setPatternType] = useState<'single' | 'alternating'>('alternating');
    const [userId, setUserId] = useState('');
    const [controller1Id, setController1Id] = useState('');
    const [controller2Id, setController2Id] = useState('');
    const [startDateInput, setStartDateInput] = useState(format(startDate, 'yyyy-MM-dd'));
    const [weeks, setWeeks] = useState(12);
    const [shiftType, setShiftType] = useState('CONTROL');
    const [loading, setLoading] = useState(false);
    const [conflicts, setConflicts] = useState<PatternConflict | null>(null);
    const [controllers, setControllers] = useState<Array<{ id: string; name: string; email: string }>>([]);
    const [allUsers, setAllUsers] = useState<Array<{ id: string; name: string; email: string; role: { name: string } }>>([]);
    const [loadingControllers, setLoadingControllers] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadData = async () => {
            setLoadingControllers(true);
            setError(''); // Clear any previous errors
            try {
                // Load controllers for alternating pattern
                const controllersData = await findControllers();
                
                // Handle response - could be array directly or wrapped in data property
                const controllers = Array.isArray(controllersData) 
                    ? controllersData 
                    : ((controllersData as any)?.data || []);
                
                setControllers(controllers);
                
                // Auto-select first two controllers if available
                if (controllers.length >= 2) {
                    setController1Id(controllers[0].id);
                    setController2Id(controllers[1].id);
                } else if (controllers.length === 1) {
                    setController1Id(controllers[0].id);
                }

                // Load all users for single user pattern
                try {
                    const usersResponse = await apiClient.get('/users', { params: { limit: 100 } });
                    const users = usersResponse.data?.data || usersResponse.data?.users || [];
                    setAllUsers(users);
                } catch (userError: any) {
                    console.warn('Failed to load users for single pattern', userError);
                    // Don't fail the whole modal if users can't load
                }
            } catch (error: any) {
                console.error('Failed to load controllers', error);
                const errorMessage = error.response?.data?.error?.message 
                    || error.response?.data?.message 
                    || error.message 
                    || 'Failed to load controllers. Please check your connection and try again.';
                setError(errorMessage);
            } finally {
                setLoadingControllers(false);
            }
        };
        loadData();
    }, []);

    useEffect(() => {
        const checkConflicts = async () => {
            if (patternType === 'single' && userId) {
                try {
                    const result = await detectPatternConflicts({
                        userId,
                        startDate: startDateInput,
                        weeks,
                    });
                    setConflicts(result);
                } catch (error) {
                    console.error('Failed to check conflicts', error);
                }
            }
        };
        checkConflicts();
    }, [patternType, userId, startDateInput, weeks]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const data: any = {
                startDate: new Date(startDateInput).toISOString(),
                weeks,
                type: shiftType,
            };

            if (versionId) {
                data.versionId = versionId;
            }

            if (patternType === 'alternating') {
                if (!controller1Id || !controller2Id) {
                    setError('Both controllers required for alternating pattern');
                    setLoading(false);
                    return;
                }
                data.controller1Id = controller1Id;
                data.controller2Id = controller2Id;
            } else {
                if (!userId) {
                    setError('User ID required');
                    setLoading(false);
                    return;
                }
                data.userId = userId;
            }

            await generatePattern(data);
            onSuccess();
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to generate pattern');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-bold mb-4">Generate Shift Pattern</h3>

                {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-2">Pattern Type</label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setPatternType('alternating')}
                                className={`flex-1 px-4 py-2 rounded ${patternType === 'alternating' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
                            >
                                Alternating (2 Controllers)
                            </button>
                            <button
                                type="button"
                                onClick={() => setPatternType('single')}
                                className={`flex-1 px-4 py-2 rounded ${patternType === 'single' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
                            >
                                Single User
                            </button>
                        </div>
                    </div>

                    {patternType === 'alternating' ? (
                        <>
                            {loadingControllers ? (
                                <div className="text-center py-4 text-gray-500">Loading controllers...</div>
                            ) : error && !controllers.length ? (
                                <div className="bg-red-50 border border-red-200 rounded p-3 text-red-800 text-sm">
                                    {error}
                                </div>
                            ) : controllers.length < 2 ? (
                                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-yellow-800 text-sm">
                                    {controllers.length === 0 
                                        ? 'No controllers found. Please ensure at least 2 users have the CONTROLLER role.'
                                        : `Only ${controllers.length} controller found. Need at least 2 controllers for alternating pattern.`
                                    }
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Controller 1</label>
                                        <select
                                            value={controller1Id}
                                            onChange={(e) => setController1Id(e.target.value)}
                                            className="w-full border rounded p-2"
                                            required
                                        >
                                            <option value="">Select Controller 1</option>
                                            {controllers.map((controller) => (
                                                <option 
                                                    key={controller.id} 
                                                    value={controller.id}
                                                    disabled={controller.id === controller2Id}
                                                >
                                                    {controller.name} ({controller.email})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Controller 2</label>
                                        <select
                                            value={controller2Id}
                                            onChange={(e) => setController2Id(e.target.value)}
                                            className="w-full border rounded p-2"
                                            required
                                        >
                                            <option value="">Select Controller 2</option>
                                            {controllers.map((controller) => (
                                                <option 
                                                    key={controller.id} 
                                                    value={controller.id}
                                                    disabled={controller.id === controller1Id}
                                                >
                                                    {controller.name} ({controller.email})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium mb-2">Select User</label>
                            {loadingControllers ? (
                                <div className="text-sm text-gray-500 py-2">Loading users...</div>
                            ) : (
                                <select
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    className="w-full border rounded p-2"
                                    required
                                >
                                    <option value="">Select a user</option>
                                    {allUsers.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.name} ({user.role.name})
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    <div>
                        <DatePicker
                            label="Start Date"
                            value={startDateInput}
                            onChange={setStartDateInput}
                            placeholder="Pick start date"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">Duration (Weeks)</label>
                        <input
                            type="number"
                            value={weeks}
                            onChange={(e) => setWeeks(parseInt(e.target.value))}
                            className="w-full border rounded p-2"
                            min="1"
                            max="52"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">Shift Type</label>
                        <select
                            value={shiftType}
                            onChange={(e) => setShiftType(e.target.value)}
                            className="w-full border rounded p-2"
                        >
                            <option value="CONTROL">Control</option>
                            <option value="LGW">LGW</option>
                            <option value="OFFICE">Office</option>
                            <option value="WAREHOUSE">Warehouse</option>
                        </select>
                    </div>

                    {conflicts && conflicts.hasConflicts && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                            <h4 className="font-semibold text-yellow-800 mb-2">Conflicts Detected</h4>
                            <ul className="list-disc list-inside text-sm text-yellow-700">
                                {conflicts.conflicts.map((conflict, idx) => (
                                    <li key={idx}>{conflict}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            disabled={loading}
                        >
                            {loading ? 'Generating...' : 'Generate Pattern'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PatternGenerationModal;

